import { useEffect, useRef, useState } from 'react';
import {
  access_code_base,
  access_code_1,
  access_code_2,
  access_code_3,
  access_code_4,
  access_code_5,
  access_code_6,
  access_code_enter,
  access_code_clear,
} from '../../images';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    el.isContentEditable
  );
};

const MAX_LEN = 6;

const CodeDock = ({
  revealed,
  setRevealed,
  pressedKey,
  setPressedKey,
  onSubmitCode,
  exiting = false,
  phase = 'idle',
}) => {
  const [entered, setEntered] = useState(false);

  const [armed, setArmed] = useState(false);
  const [code, setCode] = useState('');
  const wrapRef = useRef(null);

  const noiseCanvasRef = useRef(null);
  const rafRef = useRef(null);

  const flashPress = (key) => {
    setPressedKey?.(key);
    window.clearTimeout(flashPress._t);
    flashPress._t = window.setTimeout(() => setPressedKey?.(null), 110);
  };

  const submit = () => {
    flashPress('enter');

    if (code.length !== MAX_LEN) {
      // not enough digits yet — just ignore (or clear if you want)
      return;
    }

    const ok = onSubmitCode ? onSubmitCode(code) : false;

    if (!ok) {
      // wrong code: clear and let them try again
      setCode('');
      flashPress('clear');
    }
  };

  const handleAction = (key) => {
    // allow 0–6 (keyboard), but only 1–6 have visual press layers
    if (key >= '0' && key <= '6') {
      setCode((prev) => (prev.length >= MAX_LEN ? prev : prev + key));
      if (key !== '0') flashPress(key);
      return;
    }

    if (key === 'backspace') {
      setCode((prev) => prev.slice(0, -1));
      flashPress('clear');
      return;
    }

    if (key === 'clear') {
      setCode('');
      flashPress('clear');
      return;
    }

    if (key === 'enter') {
      submit();
    }
  };

  const arm = () => {
    if (exiting) return;
    setArmed(true);
    setRevealed?.(true);
  };
  const disarm = () => setArmed(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (exiting) return;
      if (!revealed || !armed) return;
      if (isTypingTarget(document.activeElement)) return;

      const k = e.key;

      // accept 0–6
      if (k >= '0' && k <= '6') {
        e.preventDefault();
        handleAction(k);
        return;
      }

      if (k === 'Enter') {
        e.preventDefault();
        handleAction('enter');
        return;
      }

      if (k === 'Backspace') {
        e.preventDefault();
        handleAction('backspace');
        return;
      }

      if (k === 'Escape') {
        e.preventDefault();
        handleAction('clear');
      }
    };

    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [revealed, armed, code, exiting]); // keep as-is

  useEffect(() => {
    const c = noiseCanvasRef.current;
    if (!c) return;

    const ctx = c.getContext('2d');
    if (!ctx) return;

    const cssW = c.width;
    const cssH = c.height;

    let running = true;

    const draw = () => {
      if (!running) return;

      const img = ctx.createImageData(cssW, cssH);
      const d = img.data;

      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        d[i] = v;
        d[i + 1] = v;
        d[i + 2] = v;
        d[i + 3] = 22;
      }

      ctx.putImageData(img, 0, 0);

      rafRef.current = window.setTimeout(() => {
        rafRef.current = requestAnimationFrame(draw);
      }, 80);
    };

    draw();

    return () => {
      running = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        clearTimeout(rafRef.current);
      }
    };
  }, []);

  // useEffect(() => {
  //   const c = noiseCanvasRef.current;
  //   if (!c) return;

  //   const resize = () => {
  //     const rect = c.getBoundingClientRect();
  //     const dpr = window.devicePixelRatio || 1;

  //     c.width = Math.max(1, Math.round(rect.width * dpr));
  //     c.height = Math.max(1, Math.round(rect.height * dpr));
  //   };

  //   resize();
  //   window.addEventListener('resize', resize);

  //   return () => window.removeEventListener('resize', resize);
  // }, []); //scratchy effect on screen

  return (
    <div
      className={`codeDock
        ${revealed ? 'revealed' : ''}
        ${armed ? 'armed' : ''}
        ${entered ? 'entered' : ''}
        ${exiting ? 'exiting' : ''}
        dock-${phase}
      `}
      onMouseEnter={arm}
      onMouseLeave={disarm}
      onMouseDown={(e) => {
        if (exiting) return;
        e.preventDefault();
        arm();
        wrapRef.current?.focus();
      }}
    >
      <div
        ref={wrapRef}
        className="codePanelWrap"
        tabIndex={exiting ? -1 : 0}
        onFocus={arm}
        onBlur={disarm}
        onAnimationEnd={(e) => {
          if (e.animationName === 'dock-enter-heavy') {
            setEntered(true);
          }
        }}
      >
        <img
          src={access_code_base}
          alt="access code"
          className="codePanel"
          draggable={false}
        />

        <div className="codeScreen" aria-hidden={!revealed}>
          <canvas
            ref={noiseCanvasRef}
            className="codeNoiseCanvas"
            width="160"
            height="52"
          />
          <div className="codeScreenText" aria-label={code}>
            {code.split('').map((ch, i) => (
              <span key={`${ch}-${i}`} className="codeChar">
                {ch}
              </span>
            ))}
            {code.length < 6 && <span className="codeCursor">▮</span>}
          </div>
        </div>

        <div className="codeOverlay" aria-hidden={!revealed}>
          <img
            src={access_code_1}
            className={`codeLayer ${pressedKey === '1' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_2}
            className={`codeLayer ${pressedKey === '2' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_3}
            className={`codeLayer ${pressedKey === '3' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_4}
            className={`codeLayer ${pressedKey === '4' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_5}
            className={`codeLayer ${pressedKey === '5' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_6}
            className={`codeLayer ${pressedKey === '6' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_enter}
            className={`codeLayer ${pressedKey === 'enter' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
          <img
            src={access_code_clear}
            className={`codeLayer ${pressedKey === 'clear' ? 'pressed' : ''}`}
            alt=""
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeDock;
